import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {Fragment, useEffect, useState} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {Divider, FormLabel} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {obsStyles} from '../obsStyle';

function ObsNeuroPupils(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const ratio = window.devicePixelRatio || window.screen.availWidth / document.documentElement.clientWidth;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [reactionLeft, setReactionLeft] = React.useState(props.reactionLeft);
    const [sizeLeft, setSizeLeft] = React.useState(props.sizeLeft);
    const [closedLeft, setClosedLeft] = React.useState(props.closedLeft);
    const [reactionRight, setReactionRight] = React.useState(props.reactionRight);
    const [sizeRight, setSizeRight] = React.useState(props.sizeRight);
    const [closedRight, setClosedRight] = React.useState(props.closedRight);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [browserZoom, setBrowserZoom] = useState(false);

    function setDefaults() {
        setSizeLeft(1);
        setReactionLeft(null);
        setClosedLeft(false);
        setSizeRight(1);
        setReactionRight(null);
        setClosedRight(false);
    }

    const detectZoom = () => {
        var newRatio = window.devicePixelRatio || window.screen.availWidth / document.documentElement.clientWidth;
        if (newRatio !== ratio) setBrowserZoom(true);
        else setBrowserZoom(false);
    };

    useEffect(() => {
        detectZoom();
        window.addEventListener('resize', detectZoom);
        return () => {
            window.removeEventListener('resize', detectZoom);
        };
    }, []);

    useEffect(() => {
        if (_.isUndefined(sizeLeft)) {
            setDefaults();
        }
    }, [props.sizeLeft]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = ((!_.isNull(reactionLeft) || !_.isNull(reactionRight) || closedLeft || closedRight) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) acceptObs();
    }, [reactionLeft, reactionRight, closedLeft, closedRight, status]);

    useEffect(() => {
        if (reactionRight === false && reactionLeft === false) setStatus(obsStatuses.noReaction);
    }, [reactionLeft, reactionRight]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    reactionLeft,
                    sizeLeft,
                    closedLeft,
                    reactionRight,
                    sizeRight,
                    closedRight,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        reactionLeft,
        sizeLeft,
        closedLeft,
        reactionRight,
        sizeRight,
        closedRight,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [
        status,
        reactionLeft,
        sizeLeft,
        closedLeft,
        reactionRight,
        sizeRight,
        closedRight,
    ]);

    const onEyeClosedLeftChange = (value) => {
        setClosedLeft(value);
        if (closedLeft) {
            setDefaults();
        }
        clearAccepted();
    };

    const onSizeLeftChange = (value) => {
        setSizeLeft(value);
        clearAccepted();
    };

    const onReactionLeftChange = (value) => {
        // console.log(value);
        setReactionLeft(value);
        clearAccepted();
    };

    const onEyeClosedRightChange = (value) => {
        setClosedRight(!closedRight);
        if (closedRight) {
            setDefaults();
        }
        clearAccepted();
    };

    const onSizeRightChange = (value) => {
        setSizeRight(value);
        clearAccepted();
    };

    const onReactionRightChange = (value) => {
        setReactionRight(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.component}>
                        <Grid item xs={12} style={{marginBottom: 5}}>
                            <FormLabel className={classes.label}>
                                {t('Left')}
                            </FormLabel>
                        </Grid>

                        <Grid item xs={12} className={classes.divRow}>
                            <AgyleBlockSwitch
                                onChange={(value) => onReactionLeftChange(value)}
                                value={reactionLeft}
                                customLabels={{
                                    yes: t('Reaction'),
                                    no: t('No Reaction'),
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />

                            <Divider orientation="vertical" flexItem className={classes.dividerVertical}/>

                            <AgyleBlockSwitch
                                onChange={onEyeClosedLeftChange}
                                value={closedLeft}
                                customLabels={{
                                    yes: t('Yes'),
                                    no: t('No'),
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />

                            <FormLabel className={classes.label} style={{marginLeft: 5}}>
                                {t('Eyes closed')}
                            </FormLabel>
                        </Grid>

                        {browserZoom && (
                            <Grid item xs={12}>
                                <div className={classes.consciousnessWarning}>
                                    {t('Pupils size scales are hidden because the page zoom is detected.')}
                                </div>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Grid
                                container
                                direction={'row'}
                                justifyContent="center"
                                alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {sizeLeft > 1 &&
                                            <>
                                                {!browserZoom &&
                                                    <svg height="150" width="150">
                                                        <g>
                                                            <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                            <circle cx={70} cy={70}
                                                                    r={(sizeLeft ? sizeLeft - 1 : 1) * 2.7}
                                                                    fill="black"/>
                                                        </g>
                                                    </svg>
                                                }
                                                <span>
                                                {t(`Pupil Size: ${sizeLeft ? sizeLeft - 1 : 1}`)}
                                            </span>
                                            </>
                                        }
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {!browserZoom &&
                                            <svg height="150" width="150">
                                                <g>
                                                    <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                    <circle cx={70} cy={70} r={(sizeLeft ? sizeLeft : 1) * 2.7}
                                                            fill="black"/>
                                                </g>
                                            </svg>
                                        }
                                        <span>
                                        {t(`Pupil Size: ${sizeLeft ? sizeLeft : 1}`)}
                                    </span>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {sizeLeft < 8 &&
                                            <>
                                                {!browserZoom &&
                                                    <svg height="150" width="150">
                                                        <g>
                                                            <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                            <circle cx={70} cy={70}
                                                                    r={(sizeLeft ? sizeLeft + 1 : 2) * 2.7}
                                                                    fill="black"/>
                                                        </g>
                                                    </svg>
                                                }
                                                <span>
                                                {t(`Pupil Size: ${sizeLeft ? sizeLeft + 1 : 2}`)}
                                            </span>
                                            </>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider style={{margin: '10px 0'}}/>

                        <Grid item xs={12}>
                            <ObsNumericStepper
                                value={sizeLeft}
                                minValue={1}
                                maxValue={8}
                                label={t('Size')}
                                onChange={onSizeLeftChange}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} className={classes.component}>
                        <Grid item xs={12}>
                            <FormLabel className={classes.label}>
                                {t('Right')}
                            </FormLabel>
                        </Grid>

                        <Grid item xs={12} className={classes.divRow}>
                            <AgyleBlockSwitch
                                onChange={(value) => onReactionRightChange(value)}
                                value={reactionRight}
                                customLabels={{
                                    yes: t('Reaction'),
                                    no: t('No Reaction'),
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />

                            <Divider orientation="vertical" flexItem className={classes.dividerVertical}/>

                            <AgyleBlockSwitch
                                onChange={onEyeClosedRightChange}
                                value={closedRight}
                                customLabels={{
                                    yes: t('Yes'),
                                    no: t('No'),
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />

                            <FormLabel className={classes.label} style={{marginLeft: 5}}>
                                {t('Eyes closed')}
                            </FormLabel>
                        </Grid>

                        {browserZoom && (
                            <Grid item xs={12}>
                                <div className={classes.consciousnessWarning}>
                                    {t('Pupils size scales are hidden because the page zoom is detected.')}
                                </div>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Grid
                                container
                                direction={'row'}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {sizeRight > 1 &&
                                            <>
                                                {!browserZoom &&
                                                    <svg height="150" width="150">
                                                        <g>
                                                            <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                            <circle cx={70} cy={70}
                                                                    r={(sizeRight ? sizeRight - 1 : 1) * 2.7}
                                                                    fill="black"/>
                                                        </g>
                                                    </svg>
                                                }
                                                <span>
                                                {t(`Pupil Size: ${sizeRight ? sizeRight - 1 : 1}`)}
                                              </span>
                                            </>
                                        }
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {!browserZoom &&
                                            <svg height="150" width="150">
                                                <g>
                                                    <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                    <circle cx={70} cy={70} r={(sizeRight ? sizeRight : 1) * 2.7}
                                                            fill="black"/>
                                                </g>
                                            </svg>
                                        }
                                        <span>
                                        {t(`Pupil Size: ${sizeRight ? sizeRight : 1}`)}
                                    </span>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Grid
                                        container
                                        direction={'column'}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        {sizeRight < 8 &&
                                            <Fragment>
                                                {!browserZoom &&
                                                    <svg height="150" width="150">
                                                        <g>
                                                            <circle cx={70} cy={70} r={32} fill="white" stroke="black"/>
                                                            <circle cx={70} cy={70}
                                                                    r={(sizeRight ? sizeRight + 1 : 2) * 2.7}
                                                                    fill="black"
                                                            />
                                                        </g>
                                                    </svg>
                                                }
                                                <span>
                                                {t(`Pupil Size: ${sizeRight ? sizeRight + 1 : 2}`)}
                                            </span>
                                            </Fragment>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider style={{margin: '10px 0'}}/>

                        <Grid item xs={12}>
                            <ObsNumericStepper
                                value={sizeRight}
                                minValue={1}
                                maxValue={8}
                                label={t('Size')}
                                onChange={onSizeRightChange}
                            />
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: !closedLeft && !(sizeLeft && !_.isNull(reactionLeft)) && !closedRight && !(sizeRight && !_.isNull(reactionRight)),
                        hideStatuses: true,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroPupils.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsNeuroPupils.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsNeuroPupils);